import React from 'react';
import { render, cleanup, act } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { enableMocking, disableMocking, MOCK_DB } from '../mockApi';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

async function renderWithMock(element, assertions, waitMs = 400) {
  enableMocking();
  let snapshot = null;
  const logCapture = createLogCapture();
  try {
    let result;
    await act(async () => { result = render(element); });
    await act(async () => { await new Promise(r => setTimeout(r, waitMs)); });
    await act(async () => { await new Promise(r => setTimeout(r, 100)); });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    disableMocking();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

export const pipelineTests = [

  // ─── ResearchPipeline list page ─────────────────────────────────────────────

  {
    id: 'pipeline-page-renders',
    name: 'ResearchPipeline page: renders without crashing',
    run: async () => {
      const ResearchPipeline = require('../../Pages/Private/Settings/ResearchPipeline/ResearchPipeline').default;
      return await renderWithMock(
        withWrapper(<ResearchPipeline />, '/research-pipeline'),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="pipeline-page"]') !== null, 'pipeline-page container must render');
        }
      );
    },
  },

  {
    id: 'pipeline-page-card-count',
    name: 'ResearchPipeline page: shows one card per pipeline returned by API',
    run: async () => {
      const ResearchPipeline = require('../../Pages/Private/Settings/ResearchPipeline/ResearchPipeline').default;
      return await renderWithMock(
        withWrapper(<ResearchPipeline />, '/research-pipeline'),
        async ({ container }) => {
          const cards = container.querySelectorAll('[data-testid^="pipeline-card-"]');
          assert(
            cards.length === MOCK_DB.pipelines.length,
            `Expected ${MOCK_DB.pipelines.length} pipeline cards, found ${cards.length}`
          );
        }
      );
    },
  },

  {
    id: 'pipeline-page-card-names',
    name: 'ResearchPipeline page: each pipeline card shows its name',
    run: async () => {
      const ResearchPipeline = require('../../Pages/Private/Settings/ResearchPipeline/ResearchPipeline').default;
      return await renderWithMock(
        withWrapper(<ResearchPipeline />, '/research-pipeline'),
        async ({ container }) => {
          MOCK_DB.pipelines.forEach(p => {
            const card = container.querySelector(`[data-testid="pipeline-card-${p.uuid}"]`);
            assert(card !== null, `Pipeline card for "${p.name}" (${p.uuid}) must exist`);
            assert(card.textContent.includes(p.name), `Card must show pipeline name "${p.name}"`);
          });
        }
      );
    },
  },

  {
    id: 'pipeline-page-research-description',
    name: 'ResearchPipeline page: pipeline cards show research description',
    run: async () => {
      const ResearchPipeline = require('../../Pages/Private/Settings/ResearchPipeline/ResearchPipeline').default;
      return await renderWithMock(
        withWrapper(<ResearchPipeline />, '/research-pipeline'),
        async ({ container }) => {
          MOCK_DB.pipelines.forEach(p => {
            if (p.research_description) {
              const card = container.querySelector(`[data-testid="pipeline-card-${p.uuid}"]`);
              assert(card !== null, `Pipeline card ${p.uuid} must exist`);
              assert(
                card.textContent.includes(p.research_description),
                `Card for "${p.name}" must show research description`
              );
            }
          });
        }
      );
    },
  },

  {
    id: 'pipeline-page-project-names',
    name: 'ResearchPipeline page: pipeline cards show associated project name',
    run: async () => {
      const ResearchPipeline = require('../../Pages/Private/Settings/ResearchPipeline/ResearchPipeline').default;
      return await renderWithMock(
        withWrapper(<ResearchPipeline />, '/research-pipeline'),
        async ({ container }) => {
          MOCK_DB.pipelines.filter(p => p.project?.name).forEach(p => {
            const card = container.querySelector(`[data-testid="pipeline-card-${p.uuid}"]`);
            assert(card !== null, `Pipeline card ${p.uuid} must exist`);
            assert(
              card.textContent.includes(p.project.name),
              `Card for "${p.name}" must show project name "${p.project.name}"`
            );
          });
        }
      );
    },
  },

  // ─── PipelineCard standalone ─────────────────────────────────────────────────

  {
    id: 'pipeline-card-completed',
    name: 'PipelineCard: completed pipeline renders with correct status',
    run: async () => {
      const PipelineCard = require('../../Components/ResearchPipeline/PipelineCard').default;
      const completedPipeline = MOCK_DB.pipelines.find(p => p.status === 'completed');
      return await renderWithMock(
        withWrapper(<PipelineCard pipeline={completedPipeline} onClick={() => {}} />, '/research-pipeline'),
        async ({ container }) => {
          const card = container.querySelector(`[data-testid="pipeline-card-${completedPipeline.uuid}"]`);
          assert(card !== null, 'Completed pipeline card must render');
          assert(card.textContent.includes(completedPipeline.name), 'Card must show pipeline name');
        },
        100
      );
    },
  },

  {
    id: 'pipeline-card-processing',
    name: 'PipelineCard: processing pipeline renders with correct status',
    run: async () => {
      const PipelineCard = require('../../Components/ResearchPipeline/PipelineCard').default;
      const processingPipeline = MOCK_DB.pipelines.find(p => p.status === 'processing');
      return await renderWithMock(
        withWrapper(<PipelineCard pipeline={processingPipeline} onClick={() => {}} />, '/research-pipeline'),
        async ({ container }) => {
          const card = container.querySelector(`[data-testid="pipeline-card-${processingPipeline.uuid}"]`);
          assert(card !== null, 'Processing pipeline card must render');
          assert(card.textContent.includes(processingPipeline.name), 'Card must show pipeline name');
        },
        100
      );
    },
  },

  {
    id: 'pipeline-empty-state',
    name: 'ResearchPipeline page: shows empty state when no pipelines exist',
    run: async () => {
      // Temporarily override the pipeline mock to return no pipelines
      enableMocking();
      const { default: ResearchPipeline } = require('../../Pages/Private/Settings/ResearchPipeline/ResearchPipeline');
      disableMocking();

      const axios = require('axios');
      const originalAdapter = axios.defaults.adapter;
      axios.defaults.adapter = (config) => new Promise(resolve => setTimeout(() => resolve({
        data: { success: true, pipelines: [] },
        status: 200, statusText: 'OK',
        headers: { 'content-type': 'application/json' },
        config, request: {},
      }), 30));

      let snapshot = null;
      const logCapture = createLogCapture();
      try {
        let result;
        await act(async () => { result = render(withWrapper(<ResearchPipeline />, '/research-pipeline')); });
        await act(async () => { await new Promise(r => setTimeout(r, 400)); });
        await act(async () => { await new Promise(r => setTimeout(r, 100)); });
        assert(result.container.querySelector('[data-testid="pipeline-empty-state"]') !== null, 'Empty state must render when no pipelines');
        assert(result.container.querySelector('[data-testid="pipeline-create-btn"]') !== null, 'Create button must exist in empty state');
        snapshot = captureComponentSnapshot(result.container);
      } finally {
        cleanup();
        axios.defaults.adapter = originalAdapter;
        logCapture.restore();
      }
      return { snapshot, logs: logCapture.getLogs() };
    },
  },

];
